import { useState } from "react";
import { toast } from "sonner";
import { Plus, Globe, ChevronUp, ChevronDown, Pencil, Trash2, Archive, RotateCcw } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useCrud } from "@/hooks/useCrud";
import { useUser } from "@/context/UserContext";
import { PLANET_STATUS, PLANET_COLORS, productBadge } from "@/data/productOptions";

const MASCOT = "/commando-mascot-v2.webp";

const FIELDS = [
  { name: "name", label: "Planet name", type: "text", placeholder: "e.g. Ocean Kingdom", required: true },
  { name: "description", label: "Short description", type: "textarea", placeholder: "What is this world about?" },
  { name: "color", label: "Colour identifier", type: "select", placeholder: "Pick a colour", options: PLANET_COLORS },
  { name: "status", label: "Status", type: "select", placeholder: "Status", options: PLANET_STATUS, required: true },
  { name: "notes", label: "Notes", type: "textarea", placeholder: "Any other detail" },
];

export const PlanetsPanel = ({ modules, onChange }) => {
  const { canWrite } = useUser();
  const { items: planets, save, remove, patch, refetch } = useCrud("planets", "Planet");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const visible = planets.filter((p) => (showArchived ? p.status === "Archived" : p.status !== "Archived"));

  const moduleCount = (planetId) => modules.filter((m) => m.planet_id === planetId && m.status !== "Archived").length;

  const openAdd = () => { setEditing({ status: "Planned", color: "#0EA5A4" }); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); setModalOpen(true); };

  const handleSave = async (v) => { await save(v); await refetch(); onChange?.(); };

  const move = async (index, dir) => {
    const arr = [...visible];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    const others = planets.filter((p) => !visible.some((v) => v.id === p.id));
    await api.reorder("planets", [...arr, ...others].map((p) => p.id));
    await refetch();
    toast.success("Order updated");
  };

  const doDelete = async () => {
    try {
      await remove(confirmId, deleteReason);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete planet.");
    }
    setConfirmId(null);
  };

  return (
    <SectionCard
      testid="planets-card"
      accent="#0EA5A4"
      eyebrow="Worlds of the Edjuverse"
      icon={Globe}
      title="Planets"
      subtitle="The top level of the content hierarchy. Reorder with the arrows."
      action={
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]">
            <Switch data-testid="planets-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
            Show archived
          </label>
          {canWrite && <Button size="sm" data-testid="add-planet-btn" onClick={openAdd} className="rounded-full bg-[#0EA5A4] text-white hover:bg-[#0C8E8D]"><Plus size={15} /> Add Planet</Button>}
        </div>
      }
    >
      {planets.length === 0 ? (
        <EmptyState image={MASCOT} title="No planets to explore yet" description="Edju Cat is ready — create the first planet to start charting worlds of learning." actionLabel="Add planet" onAction={openAdd} />
      ) : visible.length === 0 ? (
        <p data-testid="planets-empty-filtered" className="py-10 text-center text-sm text-[#5A6B8C]">{showArchived ? "No archived planets." : "No active planets — toggle “Show archived” to see archived worlds."}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="planets-list">
          {visible.map((p, i) => {
            const c = p.color || "#0EA5A4";
            return (
              <li key={p.id} data-testid={`planet-row-${p.id}`} {...(canWrite ? { role: "button", tabIndex: 0, "aria-label": `Edit planet ${p.name}`, onClick: () => openEdit(p), onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEdit(p); } } } : {})} className={`group relative flex flex-col overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white shadow-[0_2px_12px_rgba(20,36,95,0.05)] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/50 focus-visible:ring-offset-2 ${canWrite ? "cursor-pointer hover:-translate-y-1 hover:shadow-[0_10px_28px_rgba(20,36,95,0.12)]" : ""} ${showArchived ? "opacity-70" : ""}`}>
                {/* world header band */}
                <div className="relative flex items-center gap-3 px-5 py-4" style={{ background: `linear-gradient(135deg, ${c}, ${c}CC)` }}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20 text-white ring-1 ring-white/30">
                    <Globe size={22} strokeWidth={2} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-lg font-semibold text-white">{p.name}</p>
                    <p className="text-xs font-medium text-white/80">{moduleCount(p.id)} active module{moduleCount(p.id) === 1 ? "" : "s"}</p>
                  </div>
                  {!showArchived && canWrite && (
                    <div className="flex flex-col rounded-lg bg-white/15" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`planet-up-${p.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="px-1 text-white/80 disabled:opacity-30 hover:text-white"><ChevronUp size={16} /></button>
                      <button data-testid={`planet-down-${p.id}`} disabled={i === visible.length - 1} onClick={() => move(i, 1)} className="px-1 text-white/80 disabled:opacity-30 hover:text-white"><ChevronDown size={16} /></button>
                    </div>
                  )}
                </div>
                {/* body */}
                <div className="flex flex-1 flex-col p-5">
                  <div className="mb-2"><StatusBadge status={productBadge(p.status)} label={p.status} /></div>
                  <p className="flex-1 text-sm text-[#5A6B8C]">{p.description || "No description yet."}</p>
                  {canWrite && (
                  <div className="mt-4 flex items-center gap-1 border-t border-[#EEF2FB] pt-3" onClick={(e) => e.stopPropagation()}>
                    {p.status === "Archived" ? (
                      <Button size="sm" variant="ghost" data-testid={`restore-planet-${p.id}`} onClick={() => patch(p.id, { status: "Active" }, "Planet restored").then(() => onChange?.())} className="h-8 gap-1 rounded-full text-[#0EA5A4]"><RotateCcw size={14} /> Restore</Button>
                    ) : (
                      <Button size="sm" variant="ghost" data-testid={`archive-planet-${p.id}`} onClick={() => patch(p.id, { status: "Archived" }, "Planet archived").then(() => onChange?.())} className="h-8 gap-1 rounded-full text-[#5A6B8C]"><Archive size={14} /> Archive</Button>
                    )}
                    <div className="flex-1" />
                    <Button size="icon" variant="ghost" data-testid={`edit-planet-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-full text-[#5A6B8C] hover:text-[#14245F]"><Pencil size={15} /></Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-planet-${p.id}`} onClick={() => setConfirmId(p.id)} className="h-8 w-8 rounded-full text-[#5A6B8C] hover:text-[#DC2626]"><Trash2 size={15} /></Button>
                  </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Planet" : "Add Planet"} submitLabel={editing?.id ? "Save Changes" : "Add Planet"} fields={FIELDS} initialValues={editing} onSubmit={handleSave} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this planet?" description="A planet with active modules cannot be deleted — reassign or archive its modules first." softDelete linkedCount={confirmId ? modules.filter((m) => m.planet_id === confirmId).length : 0} onConfirm={(r) => { setDeleteReason(r); doDelete(); }} />
    </SectionCard>
  );
};
